/** @jsxImportSource @emotion/react */
import { skipToken } from '@reduxjs/toolkit/query'
import Toast from 'components/atoms/Toast'
import { DENIAL_NOTE_ACTIONS, DENIAL_RESOLVE_ACTION_LABELS, DenialNoteAction } from 'components/lab/billingDenial/consts'
import { WriteOffReasonField } from 'components/lab/billingPayment/WriteOffReasonField'
import { confirm } from 'components/molecules/Confirmation'
import { useAppSelector } from 'hooks/redux'
import React from 'react'
import { Button, Modal } from 'react-bootstrap'
import ReactSelect from 'react-select'
import {
  useAppealDenialsMutation,
  useAssignDenialsMutation,
  useMoveDenialsToInProcessMutation,
  useReopenDenialsMutation,
  useResolveDenialsMutation,
  useResubmitDenialsMutation,
} from 'store/services/billingDenials'
import { useGetLabUsersQuery } from 'store/services/users'
import { BillingDenialBulkResult, BillingDenialResolveAction } from 'store/types'
import { isWriteOffReason, OTHER_WRITE_OFF_REASON } from 'ts-common/utils/writeOffReasons.utils'
import { useDebounce } from 'use-debounce'

export type DenialActionKind = 'ASSIGN' | 'ASSIGN_TO_ME' | 'UNASSIGN' | 'RESUBMIT' | DenialNoteAction | BillingDenialResolveAction

const plural = (count: number) => `${count} denial${count === 1 ? '' : 's'}`

// One toast for a bulk action: what went through, and why the rest did not.
const reportResult = (result: BillingDenialBulkResult, done: string) => {
  if (!result.failed.length) {
    Toast({ type: 'success', label: `${done}: ${plural(result.succeeded.length)}` })
    return
  }

  const [first] = result.failed
  const others = result.failed.length > 1 ? ` (+${result.failed.length - 1} more)` : ''

  Toast({
    type: result.succeeded.length ? 'warning' : 'error',
    label: `${done}: ${result.succeeded.length}, failed: ${result.failed.length}. ${first.message}${others}`,
  })
}

const reportError = (error: any, fallback: string) =>
  Toast({ type: 'error', label: error?.data?.message || fallback })

/** Lab users matching a search of at least two letters, as select options. */
export const useLabUserOptions = (search: string) => {
  const [debouncedSearch] = useDebounce(search, 300)
  const { data: users } = useGetLabUsersQuery(
    debouncedSearch.length >= 2 ? { search: debouncedSearch, page: 0, size: 50 } : skipToken,
  )

  return ((users as any)?.items ?? []).map((user: any) => ({
    value: Number(user.id),
    label: `${user.firstName} ${user.lastName || ''}`.trim(),
  })) as { value: number; label: string }[]
}

type ModalProps = { denialIds: string[]; onClose: () => void; onDone?: () => void }

const AssignDenialsModal = ({ denialIds, onClose, onDone }: ModalProps) => {
  const [assignDenials, { isLoading }] = useAssignDenialsMutation()
  const [search, setSearch] = React.useState('')
  const [chosen, setChosen] = React.useState<{ value: number; label: string } | null>(null)
  const options = useLabUserOptions(search)

  const assign = async (ownerUserId: number) => {
    try {
      const result = await assignDenials({ denialIds, ownerUserId }).unwrap()
      reportResult(result, 'Assigned')
      onDone?.()
      onClose()
    } catch (error) {
      reportError(error, 'Failed to assign')
    }
  }

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: 18 }}>Assign {plural(denialIds.length)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <label className="textGray mb-1">Assign to</label>
        <ReactSelect
          isClearable
          isSearchable
          placeholder="Search users…"
          options={options}
          value={chosen}
          inputValue={search}
          onInputChange={(value) => setSearch(value)}
          onChange={(item) => setChosen(item ? { value: Number(item.value), label: item.label } : null)}
          noOptionsMessage={() => (search.length < 2 ? 'Type at least 2 letters' : 'No users found')}
          menuPortalTarget={document.body}
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={isLoading || !chosen} onClick={() => chosen && assign(chosen.value)}>
          Assign
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// Appeal, Move to In Process and Reopen: the status changes, nothing posts.
const NoteActionModal = ({ denialIds, kind, onClose, onDone }: ModalProps & { kind: DenialNoteAction }) => {
  const [appealDenials, { isLoading: appealing }] = useAppealDenialsMutation()
  const [moveDenials, { isLoading: moving }] = useMoveDenialsToInProcessMutation()
  const [reopenDenials, { isLoading: reopening }] = useReopenDenialsMutation()
  const [note, setNote] = React.useState('')
  const copy = DENIAL_NOTE_ACTIONS[kind]
  const mutate = { APPEAL: appealDenials, IN_PROCESS: moveDenials, REOPEN: reopenDenials }[kind]

  const onSubmit = async () => {
    try {
      const result = await mutate({ denialIds, note: note.trim() || undefined }).unwrap()
      reportResult(result, copy.done)
      onDone?.()
      onClose()
    } catch (error) {
      reportError(error, 'Failed to update the denials')
    }
  }

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: 18 }}>
          {copy.title}: {plural(denialIds.length)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p css={{ fontSize: 13 }}>{copy.description}</p>
        <label className="textGray mb-1">Note (optional)</label>
        <textarea
          className="form-control"
          rows={3}
          placeholder={copy.placeholder}
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={appealing || moving || reopening} onClick={onSubmit}>
          {copy.title}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

// Each denial settles with its suggested amounts; per-line amounts are
// adjusted from the charge page.
const ResolveDenialsModal = ({
  denialIds,
  action,
  suggestedWriteOffReason,
  onClose,
  onDone,
}: ModalProps & { action: BillingDenialResolveAction; suggestedWriteOffReason?: string | null }) => {
  const [resolveDenials, { isLoading }] = useResolveDenialsMutation()
  const [reason, setReason] = React.useState(suggestedWriteOffReason ?? '')
  const [note, setNote] = React.useState('')
  const [showErrors, setShowErrors] = React.useState(false)
  const reasonGiven = isWriteOffReason(reason) && (reason !== OTHER_WRITE_OFF_REASON || !!note.trim())
  // A write-off always needs a reason; a patient push writes off only what is
  // left over the stated patient responsibility.
  const reasonMissing = action === 'WRITE_OFF' && !reasonGiven

  const onSubmit = async () => {
    if (reasonMissing) {
      setShowErrors(true)
      return
    }

    try {
      const result = await resolveDenials({
        denialIds,
        action,
        writeOffReason: isWriteOffReason(reason) ? reason : null,
        writeOffNote: note.trim() || undefined,
      }).unwrap()
      reportResult(result, action === 'WRITE_OFF' ? 'Written off' : 'Pushed to patient')
      onDone?.()
      onClose()
    } catch (error) {
      reportError(error, 'Failed to resolve the denials')
    }
  }

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header>
        <Modal.Title css={{ fontSize: 18 }}>
          {DENIAL_RESOLVE_ACTION_LABELS[action]}: {plural(denialIds.length)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p css={{ fontSize: 13 }}>
          {action === 'WRITE_OFF'
            ? 'Writes off what each denial left open on its charge and resolves it.'
            : 'Moves the patient responsibility the payer stated to the patient, writes off the rest, and resolves each denial. A denial that states none moves its whole open amount.'}
          {' '}To change the amounts per line, resolve the denial from its charge.
        </p>
        <label className="textGray mb-0">
          Write-off reason{action === 'PATIENT' ? ' (when anything is written off)' : ''}
        </label>
        <WriteOffReasonField
          compact={false}
          writeOff={1}
          reason={reason}
          note={note}
          onReasonChange={setReason}
          onNoteChange={setNote}
          hasError={showErrors && reasonMissing}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" disabled={isLoading} onClick={onSubmit}>
          {DENIAL_RESOLVE_ACTION_LABELS[action]}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

/**
 * The denial actions for a selection of one or more denials: `run` starts
 * one, `modals` renders whatever it needs. `mocked` is the charge page's
 * test-submission switch, passed on to a resubmit.
 */
export const useDenialActions = ({ mocked, onDone }: { mocked?: boolean; onDone?: () => void } = {}) => {
  const [resubmitDenials] = useResubmitDenialsMutation()
  const [assignDenials] = useAssignDenialsMutation()
  const currentUserId = useAppSelector((state) => state.init.id)
  const [open, setOpen] = React.useState<{
    kind: DenialActionKind
    denialIds: string[]
    suggestedWriteOffReason?: string | null
  } | null>(null)

  const resubmit = async (denialIds: string[]) => {
    try {
      await confirm('', {
        title: `Resubmit ${plural(denialIds.length)}?`,
        description:
          'Each claim goes to the denying payer again and its denial moves to In Process. '
          + 'Nothing posts until the payer answers; posting that answer resolves the denial. '
          + 'If the payer on the charge was corrected, the claim goes to the new payer as an Original '
          + 'and the denial closes as rebilled. '
          + 'A paper claim is only marked; print and mail it as usual.',
        yesAction: 'Resubmit',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    try {
      reportResult(await resubmitDenials({ denialIds, mocked }).unwrap(), 'Resubmitted')
      onDone?.()
    } catch (error) {
      reportError(error, 'Failed to resubmit the claims')
    }
  }

  const setOwner = async (denialIds: string[], ownerUserId: number | null) => {
    try {
      reportResult(await assignDenials({ denialIds, ownerUserId }).unwrap(), ownerUserId ? 'Assigned' : 'Unassigned')
      onDone?.()
    } catch (error) {
      reportError(error, ownerUserId ? 'Failed to assign' : 'Failed to unassign')
    }
  }

  const unassign = async (denialIds: string[]) => {
    try {
      await confirm('', {
        title: `Unassign ${plural(denialIds.length)}?`,
        description: 'Takes the current owner off. Nothing else changes.',
        yesAction: 'Unassign',
        noAction: 'Cancel',
      })
    } catch {
      return
    }

    await setOwner(denialIds, null)
  }

  // `suggestedWriteOffReason` preselects the write-off reason.
  const run = (kind: DenialActionKind, denialIds: string[], suggestedWriteOffReason?: string | null) => {
    if (!denialIds.length) {
      return
    }

    if (kind === 'RESUBMIT') {
      void resubmit(denialIds)
      return
    }

    if (kind === 'ASSIGN_TO_ME') {
      if (currentUserId) {
        void setOwner(denialIds, Number(currentUserId))
      }
      return
    }

    if (kind === 'UNASSIGN') {
      void unassign(denialIds)
      return
    }

    setOpen({ kind, denialIds, suggestedWriteOffReason })
  }

  const close = () => setOpen(null)
  const modalProps = open ? { denialIds: open.denialIds, onClose: close, onDone } : null

  const modals = modalProps && open && (
    <>
      {open.kind === 'ASSIGN' && <AssignDenialsModal {...modalProps} />}
      {open.kind in DENIAL_NOTE_ACTIONS && (
        <NoteActionModal {...modalProps} kind={open.kind as DenialNoteAction} />
      )}
      {(open.kind === 'WRITE_OFF' || open.kind === 'PATIENT') && (
        <ResolveDenialsModal
          {...modalProps}
          action={open.kind}
          suggestedWriteOffReason={open.suggestedWriteOffReason}
        />
      )}
    </>
  )

  return { run, modals, currentUserId: currentUserId ? Number(currentUserId) : null }
}
